// Componente reutilizável: lista contatos associados a um cliente via tabela
// junção comercial.cliente_contato. Permite:
//   - Listar contatos atuais (com badge ★ pra principal)
//   - Buscar e associar contato existente (autocomplete em comercial.contatos)
//   - Criar contato novo (cria + associa numa única ação)
//   - Tornar principal / mudar cargo_neste / desassociar
//
// Usado no modal "Editar Cliente" (substitui os 3 campos legacy contato_nome/email/fone).
//
// Uso:
//   const ref = useRef<ContatosVinculadosHandle>(null);
//   <ContatosVinculadosBox ref={ref} clienteId={cliente?.id ?? null} />
//   // Após salvar o cliente:
//   await ref.current?.commit(novoClienteId);
//
// Pra cliente novo (sem id ainda), as associações ficam em buffer local e são
// commitadas após o INSERT. Pra cliente existente, mudanças são sincronizadas
// no commit() (delta entre estado inicial e atual).
import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

export interface ContatoAssociado {
  id?: number;            // contato_id (undefined se contato novo a criar)
  nome: string;
  email?: string | null;
  telefone?: string | null;
  cargo?: string | null;
  // Metadata da associação:
  principal: boolean;
  cargo_neste?: string | null;
  papel?: string | null;
  // Marker interno (não persistido):
  _novo?: boolean;        // true = contato a criar
}

export interface ContatosVinculadosHandle {
  /** Sincroniza alterações com o backend. Chame após o cliente ter id. */
  commit: (clienteId: number) => Promise<void>;
  /** Estado atual em memória (pra ler antes de commit). */
  getContatos: () => ContatoAssociado[];
}

interface Props {
  clienteId: number | null;
}

const ContatosVinculadosBox = forwardRef<ContatosVinculadosHandle, Props>(({ clienteId }, ref) => {
  const [contatos, setContatos] = useState<ContatoAssociado[]>([]);
  const [contatosIniciais, setContatosIniciais] = useState<ContatoAssociado[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [novoForm, setNovoForm] = useState({
    nome: '', email: '', telefone: '', cargo_neste: '', principal: false,
  });
  const buscaRef = useRef<HTMLInputElement>(null);

  // Carrega contatos vinculados ao cliente (se existir)
  useEffect(() => {
    if (!clienteId) {
      setContatos([]);
      setContatosIniciais([]);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/clientes/${clienteId}/contatos`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        const arr = (Array.isArray(data) ? data : []).map((c: any) => ({
          id: c.id,
          nome: c.nome,
          email: c.email,
          telefone: c.telefone,
          cargo: c.cargo,
          principal: !!c.principal,
          cargo_neste: c.cargo_neste,
          papel: c.papel,
        }));
        setContatos(arr);
        setContatosIniciais(arr);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [clienteId]);

  // Debounce de busca em comercial.contatos
  useEffect(() => {
    if (!busca.trim() || busca.length < 2) {
      setResultadosBusca([]);
      return;
    }
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/contatos?busca=${encodeURIComponent(busca)}`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        const arr = Array.isArray(data) ? data : [];
        // Exclui os que já estão vinculados a este cliente
        const idsAtuais = new Set(contatos.map(c => c.id).filter(Boolean));
        setResultadosBusca(arr.filter((c: any) => !idsAtuais.has(c.id)).slice(0, 8));
      } catch { setResultadosBusca([]); }
      finally { setBuscando(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [busca, contatos]);

  function adicionarExistente(c: any) {
    setContatos(prev => [...prev, {
      id: c.id, nome: c.nome, email: c.email, telefone: c.telefone, cargo: c.cargo,
      principal: false, cargo_neste: null, papel: null,
    }]);
    setBusca('');
    setResultadosBusca([]);
  }

  function adicionarNovo() {
    if (!novoForm.nome.trim()) { alert('Nome é obrigatório'); return; }
    setContatos(prev => {
      const arr = [...prev];
      // Se marcou principal, desmarca outros
      if (novoForm.principal) arr.forEach(x => { x.principal = false; });
      arr.push({
        nome: novoForm.nome.trim(),
        email: novoForm.email.trim() || null,
        telefone: novoForm.telefone.trim() || null,
        cargo: null,
        cargo_neste: novoForm.cargo_neste.trim() || null,
        principal: novoForm.principal,
        papel: null,
        _novo: true,
      });
      return arr;
    });
    setNovoForm({ nome: '', email: '', telefone: '', cargo_neste: '', principal: false });
    setCriandoNovo(false);
  }

  function tornarPrincipal(idx: number) {
    setContatos(prev => prev.map((c, i) => ({ ...c, principal: i === idx })));
  }

  function remover(idx: number) {
    setContatos(prev => prev.filter((_, i) => i !== idx));
  }

  function setCargoNeste(idx: number, valor: string) {
    setContatos(prev => prev.map((c, i) => i === idx ? { ...c, cargo_neste: valor || null } : c));
  }
  // Sprint F8: edita o papel (comercial / financeiro / ambos / null)
  function setPapel(idx: number, valor: string) {
    setContatos(prev => prev.map((c, i) => i === idx ? { ...c, papel: valor || null } : c));
  }

  // Sincroniza estado com backend
  useImperativeHandle(ref, () => ({
    getContatos: () => contatos,
    commit: async (cliId: number) => {
      const token = await getToken();
      const headers = { Authorization: token };
      // 1) Cria contatos novos (que não têm id ainda) e captura os ids resultantes
      const finais: ContatoAssociado[] = [];
      for (const c of contatos) {
        if (c._novo) {
          const r = await axios.post(`${API_URL}/comercial/contatos`, {
            action: 'criar',
            nome: c.nome, email: c.email, telefone: c.telefone,
            cliente_id: cliId,
            principal: c.principal,
            cargo: c.cargo_neste, // cargo geral do contato (fallback)
          }, { headers });
          const data = r.data.body ? JSON.parse(r.data.body) : r.data;
          finais.push({ ...c, id: data.id, _novo: false });
        } else {
          finais.push(c);
        }
      }

      const idsFinais = new Set(finais.map(c => c.id).filter(Boolean) as number[]);

      // 2) Associa os que existem mas não estavam (ou atualiza metadata se mudou)
      for (const c of finais) {
        if (!c.id) continue;
        const inicial = contatosIniciais.find(x => x.id === c.id);
        const novoVinculo = !inicial;
        const mudou = inicial && (
          inicial.principal !== c.principal ||
          (inicial.cargo_neste || null) !== (c.cargo_neste || null) ||
          (inicial.papel || null) !== (c.papel || null)  // Sprint F8: papel também dispara update
        );
        // Se já passou pelo "criar" acima, a associação foi criada lá. Pula.
        if (c._novo) continue;
        if (novoVinculo || mudou) {
          await axios.post(`${API_URL}/comercial/contatos`, {
            action: 'associar',
            cliente_id: cliId,
            contato_id: c.id,
            principal: c.principal,
            cargo_neste: c.cargo_neste,
            papel: c.papel,
          }, { headers });
        }
      }

      // 3) Desassocia os removidos
      for (const c of contatosIniciais) {
        if (c.id && !idsFinais.has(c.id)) {
          await axios.post(`${API_URL}/comercial/contatos`, {
            action: 'desassociar',
            cliente_id: cliId,
            contato_id: c.id,
          }, { headers });
        }
      }

      // Atualiza baseline pra próximo commit (mantém o componente sincronizado)
      setContatos(finais);
      setContatosIniciais(finais);
    },
  }), [contatos, contatosIniciais]);

  return (
    <div style={{
      gridColumn: '1 / -1',
      padding: 12, background: '#FAFBFF',
      border: '0.5px solid #E4E7F1', borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#020D7F' }}>
          📇 Contatos vinculados ({contatos.length}) · ★ marca principal
        </label>
        {!criandoNovo && (
          <button type="button" onClick={() => setCriandoNovo(true)}
            style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600,
              border: '0.5px solid #0930D0', borderRadius: 4, background: '#fff',
              color: '#0930D0', cursor: 'pointer' }}>
            + Novo contato
          </button>
        )}
      </div>

      {loading && <div style={{ padding: 8, fontSize: 11, color: '#888' }}>Carregando...</div>}

      {/* Lista de contatos vinculados */}
      {!loading && contatos.length === 0 && !criandoNovo && (
        <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', padding: 8 }}>
          Nenhum contato vinculado. Busque ou crie um novo abaixo.
        </div>
      )}
      {contatos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {contatos.map((c, i) => (
            <div key={(c.id ?? `novo-${i}`)} style={{
              padding: '6px 10px', background: '#fff', borderRadius: 5,
              border: c.principal ? '1px solid #0930D0' : '0.5px solid #ddd',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              {/* Linha 1: nome + cargo + principal + remover */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 110px auto auto',
                gap: 8, alignItems: 'center',
              }}>
                <span style={{ color: c.principal ? '#B07500' : '#ccc', fontSize: 14 }}>★</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: c.principal ? 600 : 500, color: '#020D7F',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.nome}
                    {c._novo && (
                      <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px',
                        background: '#0a7f4a', color: '#fff', borderRadius: 3 }}>NOVO</span>
                    )}
                  </div>
                  {(c.email || c.telefone) && (
                    <div style={{ fontSize: 10, color: '#888',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.email}{c.email && c.telefone ? ' · ' : ''}{c.telefone}
                    </div>
                  )}
                </div>
                <input value={c.cargo_neste || ''}
                  onChange={e => setCargoNeste(i, e.target.value)}
                  placeholder="Cargo aqui"
                  style={{ padding: '4px 6px', fontSize: 10, borderRadius: 3,
                    border: '0.5px solid #ddd' }} />
                {!c.principal && (
                  <button type="button" onClick={() => tornarPrincipal(i)}
                    title="Tornar principal"
                    style={{ fontSize: 10, padding: '3px 7px', borderRadius: 3,
                      border: '0.5px solid #B07500', background: '#fff',
                      color: '#B07500', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    ★ Principal
                  </button>
                )}
                {c.principal && <span style={{ width: 80 }} />}
                <button type="button" onClick={() => remover(i)}
                  title="Desassociar (não exclui o contato)"
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    color: '#B91626', fontSize: 16, padding: '0 4px' }}>×</button>
              </div>
              {/* Linha 2: papel — Sprint F8 (controla quem recebe NF/BM) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 22 }}>
                <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>Papel:</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[
                    { v: '', label: '— sem papel', cor: '#888' },
                    { v: 'comercial', label: 'Comercial', cor: '#0930D0' },
                    { v: 'financeiro', label: 'Financeiro', cor: '#B07500' },
                    { v: 'ambos', label: 'Ambos', cor: '#7C3AED' },
                  ].map(opt => {
                    const ativo = (c.papel || '') === opt.v;
                    return (
                      <button key={opt.v} type="button" onClick={() => setPapel(i, opt.v)}
                        title={opt.label}
                        style={{
                          padding: '2px 8px', fontSize: 10, borderRadius: 10,
                          background: ativo ? opt.cor : '#fff',
                          color: ativo ? '#fff' : opt.cor,
                          border: `0.5px solid ${opt.cor}`,
                          cursor: 'pointer', fontWeight: ativo ? 600 : 400,
                        }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 9, color: '#aaa', fontStyle: 'italic' }}>
                  ↳ define se recebe NF/BM
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Buscar/associar contato existente */}
      {!criandoNovo && (
        <div>
          <input ref={buscaRef}
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="🔍 Buscar contato existente por nome ou e-mail..."
            style={{ width: '100%', padding: '6px 10px', fontSize: 11,
              borderRadius: 4, border: '0.5px solid #ddd', boxSizing: 'border-box' }} />
          {(buscando || resultadosBusca.length > 0) && (
            <div style={{
              marginTop: 4, background: '#fff', border: '0.5px solid #ddd',
              borderRadius: 4, maxHeight: 200, overflowY: 'auto',
            }}>
              {buscando && <div style={{ padding: 8, fontSize: 11, color: '#888' }}>Buscando...</div>}
              {!buscando && resultadosBusca.map(r => (
                <div key={r.id}
                  onClick={() => adicionarExistente(r)}
                  style={{ padding: '6px 10px', cursor: 'pointer',
                    borderBottom: '0.5px solid #f0f0f0', fontSize: 11 }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#FAFBFF')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <div style={{ fontWeight: 500, color: '#020D7F' }}>{r.nome}</div>
                  <div style={{ fontSize: 10, color: '#666' }}>
                    {r.email || '—'} · {r.telefone || '—'}
                    {r.empresas && r.empresas.length > 0 && (
                      <> · em {r.empresas.length} empresa{r.empresas.length !== 1 ? 's' : ''}</>
                    )}
                  </div>
                </div>
              ))}
              {!buscando && resultadosBusca.length === 0 && busca.length >= 2 && (
                <div style={{ padding: 8, fontSize: 11, color: '#888' }}>
                  Nenhum contato encontrado. Clique em <strong>+ Novo contato</strong> pra criar.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form de criar contato novo */}
      {criandoNovo && (
        <div style={{ padding: 10, background: '#fff', borderRadius: 5,
          border: '0.5px solid #0930D0' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#020D7F', marginBottom: 8 }}>
            Novo contato (vinculado a esta empresa)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <input value={novoForm.nome}
              onChange={e => setNovoForm(f => ({ ...f, nome: e.target.value }))}
              placeholder="Nome *"
              style={inpBase} />
            <input value={novoForm.cargo_neste}
              onChange={e => setNovoForm(f => ({ ...f, cargo_neste: e.target.value }))}
              placeholder="Cargo nesta empresa"
              style={inpBase} />
            <input type="email" value={novoForm.email}
              onChange={e => setNovoForm(f => ({ ...f, email: e.target.value }))}
              placeholder="E-mail"
              style={inpBase} />
            <input value={novoForm.telefone}
              onChange={e => setNovoForm(f => ({ ...f, telefone: e.target.value }))}
              placeholder="Telefone"
              style={inpBase} />
          </div>
          <label style={{ fontSize: 10, color: '#555', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input type="checkbox" checked={novoForm.principal}
              onChange={e => setNovoForm(f => ({ ...f, principal: e.target.checked }))} />
            Marcar como contato principal
          </label>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setCriandoNovo(false)}
              style={{ padding: '4px 12px', fontSize: 11, borderRadius: 3,
                border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button type="button" onClick={adicionarNovo}
              style={{ padding: '4px 14px', fontSize: 11, fontWeight: 600,
                border: 'none', borderRadius: 3, background: '#0930D0',
                color: '#fff', cursor: 'pointer' }}>
              + Adicionar
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: 10, color: '#888', marginTop: 8 }}>
        💡 Mudanças só são efetivadas ao clicar <strong>Salvar</strong> no formulário do cliente.
      </div>
    </div>
  );
});

const inpBase: React.CSSProperties = {
  padding: '5px 8px', fontSize: 11, borderRadius: 3,
  border: '0.5px solid #ddd', boxSizing: 'border-box', width: '100%',
};

export default ContatosVinculadosBox;
